import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Table, Tag, Typography, Modal, Input, Select, DatePicker, message, Pagination, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { subscriptionStatus, subscriptionExpiry, fullname, role, email } from '../utils/data';
import { AdminSubscriptionsUrl, CancelSubscriptionUrl, UserSubscriptionSettingsUrl, StripeSignupCheckoutUrl, ReactivateCheckoutUrl, CreatePortalSessionUrl } from '../utils/network';
import { getAuthToken } from '../utils/functions';
import Header from '../components/Header';
import ArrowRight from '../components/svg/ArrowRight';
import ArrowRightWhite from '../components/svg/ArrowRightWhite';
import skyBg from '../assets/images/background/SkyWithClouds.png';
import skyBgMobile from '../assets/images/background/SkyWithCloudsMobile.png';
import rocksLeft from '../assets/images/background/RocksWithBird.png';
import rocksRight from '../assets/images/background/RocksWithGrass2.png';

const { Title, Text } = Typography;





const SubscriptionManagement: React.FC = () => {
  const [status, setStatus] = useState('inactive');
  const [expiry, setExpiry] = useState('');
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(5);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ left: 0, width: 0, client: 0 });
  
  const [appliedClientFilter, setAppliedClientFilter] = useState('all');
  const [appliedStatusFilter, setAppliedStatusFilter] = useState('all');
  const [appliedDateRange, setAppliedDateRange] = useState<any>(null);
  const [adminHistory, setAdminHistory] = useState<any[]>([]);
  const [userHistory, setUserHistory] = useState<any[]>([]);
  const [clientModalHistory, setClientModalHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isClientHistoryModalVisible, setIsClientHistoryModalVisible] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [selectedClientForModal, setSelectedClientForModal] = useState('');
  const navigate = useNavigate();

  const [summaryStats, setSummaryStats] = useState({
    activeUsersCount: 0,
    inactiveUsersCount: 0,
    totalAmount: 0.0,
  });

  const currentUser = {
    role: localStorage.getItem(role) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(email) || ''
  };
  const isAdmin = currentUser.role.toLowerCase() === 'admin';

  useEffect(() => {
    const currentStatus = localStorage.getItem(subscriptionStatus) || 'inactive';
    const currentExpiry = localStorage.getItem(subscriptionExpiry) || '';
    setStatus(currentStatus);
    setExpiry(currentExpiry);
  }, []);

  // Custom Scrollbar Sync Logic
  useEffect(() => {
    const tableBody = tableContainerRef.current?.querySelector('.ant-table-content');
    if (!tableBody) return;

    const onScroll = () => {
      setScrollInfo({
        left: tableBody.scrollLeft,
        width: tableBody.scrollWidth,
        client: tableBody.clientWidth
      });
    };

    tableBody.addEventListener('scroll', onScroll);
    onScroll(); // Initial sync

    window.addEventListener('resize', onScroll);
    
    return () => {
      tableBody.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [adminHistory, userHistory, loading, currentPage]);

  const fetchSubscriptionData = async (overrideParams?: { status?: string; dateRange?: any }) => {
    setLoading(true);
    const headers = getAuthToken();
    if (headers) {
      if (isAdmin) {
        try {
          const params: any = {};
          const statusVal = overrideParams?.status !== undefined ? overrideParams.status : statusFilter;
          const rangeVal = overrideParams?.dateRange !== undefined ? overrideParams.dateRange : dateRange;

          if (statusVal && statusVal !== 'all') {
            params.status = statusVal;
          }
          if (rangeVal && rangeVal[0] && rangeVal[1]) {
            params.start_date = rangeVal[0].format ? rangeVal[0].format('YYYY-MM-DD') : String(rangeVal[0]);
            params.end_date = rangeVal[1].format ? rangeVal[1].format('YYYY-MM-DD') : String(rangeVal[1]);
          }

          const res = await axios.get(AdminSubscriptionsUrl, {
            ...headers,
            params
          });
          if (res.data && !Array.isArray(res.data)) {
            setSummaryStats({
              activeUsersCount: res.data.active_users_count ?? 0,
              inactiveUsersCount: res.data.inactive_users_count ?? 0,
              totalAmount: res.data.total_amount ?? 0.0,
            });
          }
          const data = Array.isArray(res.data) ? res.data : (res.data.payment_history || res.data.results || res.data.data || []);
          setAdminHistory(data.map((item: any, index: number) => ({
            key: item.id || item.client_id || item.user_id || String(index),
            clientId: item.client_id || item.user_id || item.id || item.key,
            clientName: item.client_name || item.clientName || 'Unknown',
            clientEmail: item.client_email || item.clientEmail || '-',
            amount: typeof item.amount === 'number' ? `$${item.amount.toFixed(2)}` : (item.amount || '$0.00'),
            status: item.status || item.subscription_status || 'inactive',
            subscriptionStatus: item.subscription_status || item.status || 'inactive',
            receipt: item.payment_reference || item.receipt_ref || item.receipt || '-',
            startDate: item.start_date || item.paid_at || item.startDate || '-',
            endDate: item.end_date || item.endDate || '-'
          })));
        } catch (err) {
          console.error("Failed to fetch admin subscriptions", err);
        } finally {
          setLoading(false);
        }
      } else {
        try {
          const res = await axios.get(UserSubscriptionSettingsUrl, headers);
          if (res.data && !Array.isArray(res.data)) {
            if (res.data.subscription_status) {
              setStatus(res.data.subscription_status);
              localStorage.setItem(subscriptionStatus, res.data.subscription_status);
            }
            if (res.data.expiry_date !== undefined) {
              setExpiry(res.data.expiry_date || '');
              localStorage.setItem(subscriptionExpiry, res.data.expiry_date || '');
            }
            if (res.data.is_cancelled !== undefined || res.data.is_canceled !== undefined) {
              setIsCancelled(Boolean(res.data.is_cancelled ?? res.data.is_canceled));
            }
            if (res.data.days_left !== undefined && res.data.days_left !== null) {
              setDaysLeft(res.data.days_left);
            }
          }
          const data = Array.isArray(res.data) ? res.data : (res.data.payment_history || res.data.results || res.data.data || []);
          setUserHistory(data.map((item: any, index: number) => ({
            key: item.id || String(index),
            date: item.paid_at || item.start_date || item.startDate || item.date || '-',
            amount: typeof item.amount === 'number' ? `$${item.amount.toFixed(2)}` : (item.amount || '$0.00'),
            status: item.status || 'inactive',
            subscriptionStatus: item.subscription_status || 'inactive',
            receipt: item.payment_reference || item.receipt_ref || item.receipt || '-'
          })));
        } catch (err) {
          console.error("Failed to fetch user subscriptions", err);
        } finally {
          setLoading(false);
        }
      }
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, [isAdmin]);

  const filteredAdminHistory = adminHistory.filter(item => {
    const matchesClient = appliedClientFilter === 'all' || item.clientName === appliedClientFilter;
    const matchesStatus = appliedStatusFilter === 'all' || 
      item.status?.toLowerCase() === appliedStatusFilter.toLowerCase() || 
      item.subscriptionStatus?.toLowerCase() === appliedStatusFilter.toLowerCase() ||
      (appliedStatusFilter.toLowerCase() === 'succeeded' && (item.subscriptionStatus?.toLowerCase() === 'active' || item.status?.toLowerCase() === 'succeeded' || item.subscriptionStatus?.toLowerCase() === 'paid')) ||
      (appliedStatusFilter.toLowerCase() === 'failed' && (item.subscriptionStatus?.toLowerCase() === 'inactive' || item.subscriptionStatus?.toLowerCase() === 'cancelled'));
    let matchesDate = true;
    if (appliedDateRange && appliedDateRange[0] && appliedDateRange[1]) {
      const start = appliedDateRange[0].startOf('day').valueOf();
      const end = appliedDateRange[1].endOf('day').valueOf();
      const itemStart = new Date(item.startDate).getTime();
      const itemEnd = new Date(item.endDate).getTime();
      
      const startValid = !isNaN(itemStart);
      const endValid = !isNaN(itemEnd);

      if (startValid && endValid) {
        matchesDate = (itemStart >= start && itemStart <= end) || (itemEnd >= start && itemEnd <= end);
      } else if (startValid) {
        matchesDate = itemStart >= start && itemStart <= end;
      } else if (endValid) {
        matchesDate = itemEnd >= start && itemEnd <= end;
      } else {
        matchesDate = true;
      }
    }
    return matchesClient && matchesStatus && matchesDate;
  });

  const latestFilteredAdminHistory = Object.values(
    filteredAdminHistory.reduce((acc: any, current: any) => {
      const groupKey = current.clientId || current.clientEmail || current.key || current.clientName;
      if (!acc[groupKey]) {
        acc[groupKey] = current;
      } else {
        const currentDate = new Date(current.startDate).getTime();
        const existingDate = new Date(acc[groupKey].startDate).getTime();
        // Compare if both dates are valid, else keep the first encountered one
        if (!isNaN(currentDate) && !isNaN(existingDate)) {
          if (currentDate > existingDate) {
            acc[groupKey] = current;
          }
        }
      }
      return acc;
    }, {})
  );

  const handleSearch = () => {
    setAppliedClientFilter(clientFilter);
    setAppliedStatusFilter(statusFilter);
    setAppliedDateRange(dateRange);
    fetchSubscriptionData({ status: statusFilter, dateRange: dateRange });
  };

  const handleClear = () => {
    setClientFilter('all');
    setStatusFilter('all');
    setDateRange(null);
    setAppliedClientFilter('all');
    setAppliedStatusFilter('all');
    setAppliedDateRange(null);
    fetchSubscriptionData({ status: 'all', dateRange: null });
  };

  const getDaysRemaining = (expiryDate: string) => {
    if (!expiryDate) return 0;
    const end = new Date(expiryDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    const headers = getAuthToken();
    try {
      const payload = {
        email: currentUser.email,
      };
      const response = await axios.post(CancelSubscriptionUrl, payload, headers ? headers : undefined);
      setIsCancelled(true);
      if (response?.data?.subscription_status) {
        setStatus(response.data.subscription_status);
        localStorage.setItem(subscriptionStatus, response.data.subscription_status);
      }
      message.success(response?.data?.message || 'Subscription cancelled successfully. Future billing has ended.');
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      const errorMsg = error?.response?.data?.error || error?.response?.data?.detail || error?.response?.data?.message;
      if (errorMsg) {
        message.error(errorMsg);
      } else {
        message.success('Subscription cancelled successfully. Future billing has ended.');
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const handleRenew = async () => {
    setRenewLoading(true);
    const headers = getAuthToken();
    try {
      const payload = {
        email: currentUser.email,
        fullname: currentUser.fullname,
        role: currentUser.role || 'User',
      };
      const response = await axios.post(ReactivateCheckoutUrl, payload, headers ? headers : undefined);
      const checkoutUrl = response.data?.checkout_url || response.data?.url || response.data?.checkoutUrl;

      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      } else {
        message.success(response.data?.message || 'Subscription renewed successfully');
        await fetchSubscriptionData();
      }
    } catch (error: any) {
      console.error('Failed to reactivate checkout:', error);
      const errorMsg = error?.response?.data?.error || error?.response?.data?.detail || error?.response?.data?.message;
      if (errorMsg) {
        message.error(errorMsg);
      }
    } finally {
      setRenewLoading(false);
    }
  };

  const handleUpdateBilling = async () => {
    setPortalLoading(true);
    const headers = getAuthToken();
    try {
      const response = await axios.post(CreatePortalSessionUrl, {}, headers ? headers : undefined);
      const portalUrl = response.data?.url;
      if (portalUrl) {
        window.open(portalUrl, '_blank', 'noopener,noreferrer');
      } else {
        message.error('Failed to retrieve billing portal URL.');
      }
    } catch (error: any) {
      console.error('Failed to create portal session:', error);
      const errorMsg = error?.response?.data?.error || error?.response?.data?.detail || error?.response?.data?.message;
      message.error(errorMsg || 'Failed to open billing settings.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleReactivate = () => {
    localStorage.setItem(subscriptionStatus, 'active');
    setStatus('active');
  };

  const getStatusTag = (s: string) => {
    switch(s) {
      case 'active': return <Tag color="green">Active</Tag>;
      case 'trialing': return <Tag color="blue">Trialing</Tag>;
      case 'past_due': return <Tag color="orange">Past Due</Tag>;
      case 'cancelled': return <Tag color="red">Cancelled</Tag>;
      default: return <Tag color="default">Inactive</Tag>;
    }
  };
  const renderTableStatusTag = (t: string) => {
    if (!t) return <Tag color="red">Inactive</Tag>;
    const lower = t.toLowerCase();
    const formatted = t.replace(/_/g, ' ');
    const display = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    
    if (lower === 'succeeded' || lower === 'active' || lower === 'trial_started' || lower === 'paid') {
      return <Tag color="green">{display}</Tag>;
    } else if (lower === 'trialing') {
      return <Tag color="blue">{display}</Tag>;
    } else if (lower === 'pending') {
      return <Tag color="orange">{display}</Tag>;
    } else {
      return <Tag color="red">{display}</Tag>;
    }
  };

  const formatDateOnly = (dateStr: string) => {
    if (!dateStr || dateStr === '-') return '-';
    const datePart = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr.split('T')[0];
    const d = new Date(datePart);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString();
    }
    return datePart;
  };

  const columns = [
    { title: 'Date', dataIndex: 'date', key: 'date', render: (d: string) => formatDateOnly(d) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (t: string) => renderTableStatusTag(t) },
    // { title: 'Receipt', dataIndex: 'receipt', key: 'receipt' },
  ];

  const clientHistoryColumns = [
    { title: 'Start Date', dataIndex: 'startDate', key: 'startDate', render: (d: string) => formatDateOnly(d) },
    // { title: 'End Date', dataIndex: 'endDate', key: 'endDate' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (t: string) => renderTableStatusTag(t) },
    { title: 'Receipt Ref', dataIndex: 'receipt', key: 'receipt' },
  ];

  const adminColumns = [
    { title: 'Client Name', dataIndex: 'clientName', key: 'clientName' },
    { title: 'Client Email', dataIndex: 'clientEmail', key: 'clientEmail' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount' },
    { title: 'Status', dataIndex: 'subscriptionStatus', key: 'subscriptionStatus', render: (t: string) => renderTableStatusTag(t) },
    // { title: 'Receipt Ref', dataIndex: 'receipt', key: 'receipt' },
    { title: 'Start Date', dataIndex: 'startDate', key: 'startDate', render: (d: string) => formatDateOnly(d) },
    { title: 'End Date', dataIndex: 'endDate', key: 'endDate', render: (d: string) => formatDateOnly(d) },
  ];

  return (
    <div className="relative min-h-screen w-full flex flex-col bg-gradient-to-b from-[#a4c8e1] via-[#dceaf3] to-white font-['DM_Sans',sans-serif]">
      {/* Background Overlay */}
      <div className="absolute top-0 left-0 w-full z-[1] leading-none mix-blend-multiply opacity-80 pointer-events-none">
        <img src={skyBg} alt="" className="w-full h-auto object-cover min-h-[220px] hidden md:block" />
        <img src={skyBgMobile} alt="" className="w-full h-auto object-cover md:hidden" />
      </div>

      <Header currentUser={currentUser} />

      <main className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-12 pb-12 flex-1">
        <div className="premium-card white w-full px-[18px] pt-[22px] pb-[31px] md:px-[24px] md:py-[28px] mb-[29px] md:mb-[34px]">
          <Title level={2} className="m-0 font-['Lexend',sans-serif] text-[#1F2A37] text-[18px] md:text-[24px]">Billing & Subscription</Title>
        </div>

        <div className="flex flex-col gap-6 md:gap-8">
          {isAdmin && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="premium-card white p-6 flex flex-col justify-between">
                <div className="text-[#555d67] font-['Lexend',sans-serif] text-[14px] font-medium mb-2">Active Users</div>
                <div className="text-[18px] md:text-[24px] font-bold font-['Lexend',sans-serif] text-[#10b981]">
                  {summaryStats.activeUsersCount}
                </div>
              </div>
              <div className="premium-card white p-6 flex flex-col justify-between">
                <div className="text-[#555d67] font-['Lexend',sans-serif] text-[14px] font-medium mb-2">Inactive Users</div>
                <div className="text-[18px] md:text-[24px] font-bold font-['Lexend',sans-serif] text-[#ef4444]">
                  {summaryStats.inactiveUsersCount}
                </div>
              </div>
              <div className="premium-card white p-6 flex flex-col justify-between">
                <div className="text-[#555d67] font-['Lexend',sans-serif] text-[14px] font-medium mb-2">Total Revenue</div>
                <div className="text-[18px] md:text-[24px] font-bold font-['Lexend',sans-serif] text-[#1f2a37]">
                  ${typeof summaryStats.totalAmount === 'number' ? summaryStats.totalAmount.toFixed(2) : summaryStats.totalAmount}
                </div>
              </div>
            </div>
          )}

          {!isAdmin && (
            <div className="premium-card white w-full p-6 md:p-8">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <Title level={4} className="font-['Lexend',sans-serif] text-[#1F2A37]">Subscription Status</Title>
                  <div className="flex items-center gap-3 mt-2">
                    <Text strong className="text-lg font-['Lexend',sans-serif]">EmployPro Premium</Text>
                    {getStatusTag(status)}
                    
                  </div>
                  {expiry ? (
                    <Text className="text-gray-500 block mt-2 font-['Lexend',sans-serif]">
                      {isCancelled ? (
                        `Subscription ends in ${daysLeft ?? getDaysRemaining(expiry)} days (${new Date(expiry).toLocaleDateString()})`
                      ) : status === 'trialing' ? (
                        `Free trial ends in ${daysLeft ?? getDaysRemaining(expiry)} days (${new Date(expiry).toLocaleDateString()})`
                      ) : status === 'active' ? (
                        `Renews in ${daysLeft ?? getDaysRemaining(expiry)} days (${new Date(expiry).toLocaleDateString()})`
                      ) : (
                        `Expired on ${new Date(expiry).toLocaleDateString()}`
                      )}
                    </Text>
                  ) : (
                    <Text className="text-gray-500 block mt-2 font-['Lexend',sans-serif]">
                      {daysLeft !== null ? `${daysLeft} days left` : isCancelled ? 'Subscription will end at the end of the billing period' : status === 'active' ? 'Active subscription' : 'Subscription is currently inactive'}
                    </Text>
                  )}
                </div>
                
                <div className="mt-4 md:mt-0 flex flex-wrap items-center gap-3">
                  {(() => {
                    const isRenewDisabled = !(status === 'inactive' || status === 'cancelled' || isCancelled);
                    const isCancelDisabled = isCancelled || status === 'inactive' || status === 'cancelled';
                    
                    return (
                      <>
                        <Tooltip title={isRenewDisabled ? "Cannot renew while subscription is active" : ""}>
                          <span className={isRenewDisabled ? "cursor-not-allowed inline-block" : "inline-block"}>
                            <Button 
                              type="primary" 
                              loading={renewLoading}
                              onClick={handleRenew} 
                              disabled={isRenewDisabled}
                              className={`font-['Lexend',sans-serif] ${
                                isRenewDisabled
                                  ? '!bg-[#E5E7EB] !text-[#9CA3AF] !border-none !shadow-none !cursor-not-allowed pointer-events-none' 
                                  : '!bg-[#4682B4] hover:!bg-[#3b6d98] !text-white !border-none shadow-sm hover:shadow-md'
                              }`}
                            >
                              Renew Subscription
                            </Button>
                          </span>
                        </Tooltip>

                        <Tooltip title={isCancelDisabled ? (status === 'inactive' ? "Subscription is inactive" : "Subscription is already cancelled") : ""}>
                          <span className={isCancelDisabled ? "cursor-not-allowed inline-block" : "inline-block"}>
                            <Button 
                              danger 
                              loading={cancelLoading}
                              onClick={handleCancel} 
                              disabled={isCancelDisabled}
                              className={`font-['Lexend',sans-serif] ${
                                isCancelDisabled
                                  ? '!bg-[#E5E7EB] !text-[#9CA3AF] !border-none !shadow-none !cursor-not-allowed pointer-events-none' 
                                  : '!bg-red-50 hover:!bg-red-600 !text-red-600 hover:!text-white !border !border-red-200 hover:!border-red-600 shadow-sm'
                              }`}
                            >
                              Cancel Subscription
                            </Button>
                          </span>
                        </Tooltip>
                        <Button 
                          loading={portalLoading}
                          onClick={handleUpdateBilling} 
                          className="font-['Lexend',sans-serif] !bg-[#4682B4] hover:!bg-[#3b6d98] !text-white !border-none shadow-sm hover:shadow-md"
                        >
                          Update Billing Details
                        </Button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          <div className="premium-card blue-tint w-full p-6 md:p-8">
            <Title level={4} className="mb-6 font-['Lexend',sans-serif] text-[#1F2A37]">
              {isAdmin ? 'Payment History (All Clients)' : 'Payment History'}
            </Title>
            
            {isAdmin && (
              <div className="flex flex-wrap items-end gap-4 mb-6">
                 {/* <div className="flex flex-col">
                   <span className="font-semibold text-xs mb-1 font-['Lexend',sans-serif] text-[#1F2A37]">Client Name</span>
                   <Select 
                     value={clientFilter} 
                     onChange={setClientFilter} 
                     style={{ width: 200 }}
                     options={[
                       { value: 'all', label: 'All Clients' },
                       ...Array.from(new Set(adminHistory.map(item => item.clientName))).map(name => ({ value: name, label: name }))
                     ]}
                     className="font-['Lexend',sans-serif]"
                   />
                 </div> */}
                  <div className="flex flex-col">
                    <span className="font-semibold text-xs mb-1 font-['Lexend',sans-serif] text-[#1F2A37]">Status</span>
                    <Select 
                      value={statusFilter} 
                      onChange={setStatusFilter} 
                      style={{ width: 160 }}
                      options={[
                        { value: 'all', label: 'All Statuses' },
                        { value: 'active', label: 'Active' },
                        { value: 'trialing', label: 'Trialing' },
                        { value: 'cancelled', label: 'Cancelled' },
                        { value: 'inactive', label: 'Inactive' },
                        { value: 'Succeeded', label: 'Succeeded' },
                        { value: 'Failed', label: 'Failed' },
                      ]}
                      className="font-['Lexend',sans-serif]"
                    />
                  </div>
                 <div className="flex flex-col">
                   <span className="font-semibold text-xs mb-1 font-['Lexend',sans-serif] text-[#1F2A37]">Date Range</span>
                   <DatePicker.RangePicker 
                     value={dateRange}
                     onChange={(dates) => setDateRange(dates)}
                     className="font-['Lexend',sans-serif] w-[260px]"
                   />
                 </div>
                 <Button 
                   type="primary"
                   className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-[#4682B4] hover:bg-[#3b6d96] text-white font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] border-none cursor-pointer transition-colors h-[32px] md:h-[47px]"
                   onClick={handleSearch}
                 >
                   <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] rounded-full bg-white flex items-center justify-center text-[#4682B4]">
                     <div className="scale-[0.6] md:scale-100 flex items-center justify-center"><ArrowRight /></div>
                   </div>
                   Search
                 </Button>
                 <Button 
                    className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-white border border-[#4682B4] text-[#1F2A37] font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] cursor-pointer transition-colors hover:bg-[#f8FAFC] h-[32px] md:h-[47px]"
                    onClick={handleClear}
                  >
                    <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] flex items-center justify-center">
                      <div className="scale-[0.73] md:scale-100 flex items-center justify-center origin-center"><ArrowRightWhite /></div>
                    </div>
                    Clear
                  </Button>
              </div>
            )}
            
            <style dangerouslySetInnerHTML={{
              __html: `
              .modern-table .ant-table-thead > tr > th {
                background: #4682B417 !important;
                color: #1F2A37 !important;
                font-weight: 400 !important;
                font-family: 'Lexend', sans-serif !important;
                border-bottom: none !important;
                padding: 16px !important;
                font-size: 14px !important;
              }
              .modern-table .ant-table {
                background: transparent !important;
              }
              .modern-table .ant-table-container {
                background: transparent !important;
              }
              .modern-table .ant-table-row {
                transition: all 0.2s;
                background: transparent !important;
              }
              .modern-table .ant-table-row:hover {
                background-color: #F8FAFC !important;
              }
              .modern-table .ant-table-cell {
                border-bottom: 1px solid #F1F5F9 !important;
                padding: 16px !important;
                font-family: 'Lexend', sans-serif !important;
                background: transparent !important;
                font-size: 14px !important;
              }
            `}} />
            <div ref={tableContainerRef}>
              <Table 
                className="modern-table font-['Lexend',sans-serif]"
                columns={isAdmin ? adminColumns as any : columns} 
                dataSource={(isAdmin ? latestFilteredAdminHistory : userHistory).slice((currentPage - 1) * pageSize, currentPage * pageSize) as any} 
                pagination={false}
                scroll={{ x: 1000 }}
                loading={loading}
                locale={{ emptyText: <span className="font-['Lexend',sans-serif] text-[#555d67]">No data found</span> }}
                onRow={isAdmin ? (record: any) => ({
                  onClick: async () => {
                    if (record?.clientName) {
                      setSelectedClientForModal(record.clientName);
                      setIsClientHistoryModalVisible(true);
                      setModalCurrentPage(1);
                      setModalLoading(true);
                      const headers = getAuthToken();
                      if (headers) {
                        try {
                          const targetClientId = record?.clientId || record?.id || record?.key;
                          const paramsObj: any = {};
                          if (targetClientId) {
                            paramsObj.client_id = targetClientId;
                          }
                          const res = await axios.get(UserSubscriptionSettingsUrl, {
                            ...headers,
                            params: paramsObj
                          });
                          const data = Array.isArray(res.data) ? res.data : (res.data.payment_history || res.data.results || res.data.data || []);
                          if (data && data.length > 0) {
                            setClientModalHistory(data.map((item: any, index: number) => ({
                              key: item.id || String(index),
                              startDate: item.paid_at || item.start_date || item.startDate || item.date || '-',
                              amount: typeof item.amount === 'number' ? `$${item.amount.toFixed(2)}` : (item.amount || '$0.00'),
                              status: item.status || 'inactive',
                              receipt: item.receipt || '-'
                            })));
                          } else {
                            setClientModalHistory([]);
                          }
                        } catch (err) {
                          console.error("Failed to fetch client subscription settings", err);
                          setClientModalHistory([]);
                        } finally {
                          setModalLoading(false);
                        }
                      } else {
                        setClientModalHistory([]);
                        setModalLoading(false);
                      }
                    }
                  },
                  style: { cursor: 'pointer' }
                }) : undefined}
              />
            </div>

            {/* Custom Synchronized Scrollbar */}
            {scrollInfo.width > scrollInfo.client && (
              <div className="w-full flex justify-center mt-[19px]">
                <div 
                  className="relative h-[4px] rounded-[3px] overflow-hidden" 
                  style={{ 
                    width: '85.90%', 
                    backgroundColor: '#4682B430' 
                  }}
                >
                  <div 
                    className="absolute h-full bg-[#4682B4] rounded-[3px] transition-all duration-100"
                    style={{
                      width: `${(scrollInfo.client / scrollInfo.width) * 60}%`,
                      left: `${(scrollInfo.left / (scrollInfo.width - scrollInfo.client || 1)) * (100 - (scrollInfo.client / scrollInfo.width * 60))}%`
                    }}
                  />
                </div>
              </div>
            )}

            {/* Separate Pagination outside table */}
            <div className="w-full flex justify-end mt-[22px]">
              <style dangerouslySetInnerHTML={{__html: `
                .custom-pagination-container {
                  display: flex !important;
                  align-items: center !important;
                  justify-content: flex-end !important;
                  width: 100% !important;
                }
                .custom-pagination-container .ant-pagination-total-text {
                  order: 1 !important;
                  color: #555D67 !important;
                  font-family: 'Lexend', sans-serif !important;
                  font-size: 10px !important;
                  font-weight: 300 !important;
                  margin-right: 8px !important;
                }
                @media (min-width: 768px) {
                  .custom-pagination-container .ant-pagination-total-text {
                    font-size: 14px !important;
                    margin-right: 12px !important;
                  }
                }
                .custom-pagination-container .ant-pagination-prev,
                .custom-pagination-container .ant-pagination-next {
                  order: 2 !important;
                  border: none !important;
                  background: transparent !important;
                  min-width: 20px !important;
                  height: 32px !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  color: #1F2A37 !important;
                  font-size: 16px !important;
                  margin: 0 !important;
                }
                .custom-pagination-container .ant-pagination-item,
                .custom-pagination-container .ant-pagination-jump-prev,
                .custom-pagination-container .ant-pagination-jump-next {
                  order: 2 !important;
                  border-radius: 4px !important;
                  height: 28px !important;
                  min-width: 28px !important;
                  line-height: 26px !important;
                  background: white !important;
                  font-family: 'Lexend', sans-serif !important;
                  margin: 0 4px !important;
                  font-size: 14px !important;
                  display: inline-flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                }
                .custom-pagination-container .ant-pagination-item {
                  border: 0.8px solid #4682B4 !important;
                }
                .custom-pagination-container .ant-pagination-jump-prev,
                .custom-pagination-container .ant-pagination-jump-next {
                  border: none !important;
                }
                @media (max-width: 767px) {
                  .custom-pagination-container .ant-pagination-item:not(.ant-pagination-item-active),
                  .custom-pagination-container .ant-pagination-jump-prev,
                  .custom-pagination-container .ant-pagination-jump-next {
                    display: none !important;
                  }
                }
                .custom-pagination-container .ant-pagination-item-active {
                  border-color: #4682B4 !important;
                }
                .custom-pagination-container .ant-pagination-item-active a {
                  color: #1F2A37 !important;
                  font-weight: 500 !important;
                }
                .custom-pagination-container .ant-pagination-options {
                  order: 3 !important;
                  margin-left: 8px !important;
                  display: block !important;
                }
                .custom-pagination-container .ant-select-selector {
                  background-color: #032B440D !important;
                  border: none !important;
                  border-radius: 4px !important;
                  height: 28px !important;
                  font-family: 'Lexend', sans-serif !important;
                  font-size: 12px !important;
                  font-weight: 300 !important;
                  color: #1F2A37 !important;
                  padding: 0 10px !important;
                  display: flex !important;
                  align-items: center !important;
                }
                .custom-pagination-container .ant-select-selection-item {
                  line-height: 28px !important;
                  color: #1F2A37 !important;
                }
                .custom-pagination-container .ant-select-arrow {
                  color: #1F2A37 !important;
                }
              `}} />
              <Pagination
                className="custom-pagination-container"
                current={currentPage}
                pageSize={pageSize}
                total={(isAdmin ? latestFilteredAdminHistory : userHistory).length}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                showSizeChanger
                showTotal={(total) => `Total ${total} items`}
                itemRender={(page, type, originalElement) => {
                  if (type === 'prev') return <span className="cursor-pointer font-bold px-1">{"<"}</span>;
                  if (type === 'next') return <span className="cursor-pointer font-bold px-1">{">"}</span>;
                  return originalElement;
                }}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Assets */}
      <div className="relative w-full pointer-events-none z-[2] flex justify-between items-end mt-auto">
        <img src={rocksLeft} alt="" className="w-[209px] md:w-[220px] lg:w-[250px] xl:w-[280px] h-auto object-contain object-left-bottom" />
        <img src={rocksRight} alt="" className="w-[110px] md:w-[150px] lg:w-[180px] xl:w-[210px] h-auto object-contain object-right-bottom" />
      </div>

      <Modal
        title={<span className="font-['Lexend',sans-serif]">Payment History: {selectedClientForModal}</span>}
        open={isClientHistoryModalVisible}
        onCancel={() => {
          setIsClientHistoryModalVisible(false);
          setModalCurrentPage(1);
        }}
        footer={null}
        width={800}
      >
        <Table 
          className="modern-table font-['Lexend',sans-serif]"
          columns={clientHistoryColumns} 
          dataSource={clientModalHistory.slice((modalCurrentPage - 1) * modalPageSize, modalCurrentPage * modalPageSize)} 
          loading={modalLoading}
          pagination={false}
          locale={{ emptyText: <span className="font-['Lexend',sans-serif] text-[#555d67]">No data found</span> }}
        />
        {clientModalHistory.length > 0 && (
          <div className="w-full flex justify-end mt-4">
            <Pagination
              className="custom-pagination-container"
              current={modalCurrentPage}
              pageSize={modalPageSize}
              pageSizeOptions={['5', '10']}
              total={clientModalHistory.length}
              onChange={(page, size) => {
                setModalCurrentPage(page);
                setModalPageSize(size);
              }}
              showSizeChanger
              showTotal={(total) => `Total ${total} items`}
              itemRender={(page, type, originalElement) => {
                if (type === 'prev') return <span className="cursor-pointer font-bold px-1">{"<"}</span>;
                if (type === 'next') return <span className="cursor-pointer font-bold px-1">{">"}</span>;
                return originalElement;
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionManagement;
